import * as React from 'react';
import { isInAppBrowser } from '../utils/in-app-browser';

const noop = () => {};

// Generic Recurly.js wallet wiring — TTM's mature pattern (shared-ui's own
// useWallets, "more mature" per the original build instruction), adapted to
// MTM's config shape.
//
// Every wallet instance starts its own Braintree/PayPal client that polls in
// the background, and recurly.js gives no reliable teardown for most of
// them — rebuilding on every render (or every pricing tick) floods the
// browser with live pollers until ERR_INSUFFICIENT_RESOURCES. Apple Pay and
// Venmo print the amount into their own sheet, so only those two are
// rebuilt, keyed by whether `amount` actually changed; PayPal/Google Pay
// take the amount at authorization time and are built at most once.
export default function useWallets({
  recurly, config, total, onToken = noop, onError = noop,
}) {
  const [available, setAvailable] = React.useState({
    paypal: false, google: false, apple: false, venmo: false,
  });
  const [googleReady, setGoogleReady] = React.useState(false);
  const made = React.useRef({});
  const builtFor = React.useRef({});
  const instances = React.useRef({});
  const googleButton = React.useRef(null);
  const googleButtonRef = React.useRef(null);
  const engaged = React.useRef('');
  const cb = React.useRef({ onToken, onError });
  cb.current = { onToken, onError };

  const appleCapable = typeof window !== 'undefined' && !!window.ApplePaySession;
  // Whole cents, as a "12.34" string. Step 4's total is float arithmetic
  // (first + shipping + tax), so String(total) came out as
  // "53.97840000000001" — Google Pay's transactionInfo.totalPrice must match
  // ^[0-9]+(\.[0-9][0-9])?$, and a request that doesn't is rejected by the
  // sheet itself ("This merchant is having trouble accepting your payment
  // right now [OR_BIBED_06]"), before any token or purchase exists. Apple Pay
  // and Venmo print the amount in their own sheet, so they get it too.
  const amount = total != null ? Math.round(Number(total) * 100) / 100 : null;
  const amountStr = amount != null ? amount.toFixed(2) : null;

  const wire = React.useCallback((key, source, instance) => {
    instances.current[key] = instance;
    instance.on('token', (token) => { engaged.current = ''; cb.current.onToken(source, token.id); });
    instance.on('error', (err) => {
      setAvailable((a) => (a[key] ? a : { ...a, [key]: false }));
      if (engaged.current !== key) return;
      engaged.current = '';
      // recurly.js's GooglePay has no 'cancel' event: closing Google's sheet
      // rejects like a failure does, and both arrive here as one error. Google's
      // status, copied onto it, is the only thing that tells them apart:
      // CANCELED is the customer closing the sheet.
      cb.current.onError(source, err && err.statusCode === 'CANCELED' ? null : err);
    });
    instance.on('cancel', () => {
      if (engaged.current !== key) return;
      engaged.current = '';
      cb.current.onError(source, null);
    });
  }, []);

  React.useEffect(() => {
    if (!recurly || !config) return;
    const bt = config.braintree || {};

    if (bt.paypal && !made.current.paypal) {
      made.current.paypal = true;
      const pp = recurly.PayPal({
        display: { displayName: config.displayName },
        braintree: { clientAuthorization: bt.paypal },
      });
      pp.on('ready', () => setAvailable((a) => ({ ...a, paypal: true })));
      wire('paypal', 'paypal', pp);
    }

    // Apple Pay and Google Pay are mutually exclusive by device — only one
    // is ever offered, matching legacy's `if (ApplePaySession) {...} else if
    // (...) { GooglePay }` branch.
    if (appleCapable && amount != null && builtFor.current.apple !== amount && bt.applepay) {
      builtFor.current.apple = amount;
      const ap = recurly.ApplePay({
        country: config.country, currency: config.currency, recurring: true,
        label: config.displayName, total: amountStr,
        braintree: { clientAuthorization: bt.applepay },
      });
      ap.on('ready', () => setAvailable((a) => ({ ...a, apple: true })));
      wire('apple', 'applepay', ap);
    } else if (!appleCapable && config.googleMerchantId && !made.current.google && amount != null) {
      made.current.google = true;
      const gp = recurly.GooglePay({
        currency: config.currency, country: config.country, total: amountStr,
        googleMerchantId: config.googleMerchantId, billingAddressRequired: true,
        // Google sizes its button itself ("static") unless told to fill the
        // container — and the signed-in version (G Pay | saved card •••• 1234)
        // is a different element from the plain button, so CSS on
        // .gpay-button never reached it: it stayed ~240px wide. "fill" is
        // Google's documented switch for sizing the button from the page.
        buttonOptions: { buttonSizeMode: 'fill' },
      });
      gp.on('ready', (button) => { googleButton.current = button; setGoogleReady(true); setAvailable((a) => ({ ...a, google: true })); });
      wire('google', 'googlepay', gp);
    }

    // Not offered inside Facebook / Instagram / TikTok's own browser: Venmo
    // switches to the Venmo app to approve, and coming back from it lands in
    // the phone's default browser with none of this checkout's state, so the
    // order can't finish (869f0ftak). Leaving it unbuilt keeps its row off.
    if (bt.venmo && !isInAppBrowser() && amount != null && builtFor.current.venmo !== amount) {
      builtFor.current.venmo = amount;
      const vn = recurly.Venmo({
        display: {
          amount: amountStr, currency: config.currency, displayName: config.displayName, locale: config.locale || 'en_US',
        },
        braintree: { clientAuthorization: bt.venmo, webAuthentication: true },
      });
      vn.on('ready', () => setAvailable((a) => ({ ...a, venmo: true })));
      wire('venmo', 'venmo', vn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurly, config, amount, appleCapable]);

  // Joins Google's own rendered button into the container the page hands us
  // — Google's brand terms only allow sizing that container, never
  // redrawing the button itself.
  React.useEffect(() => {
    const host = googleButtonRef.current;
    const button = googleButton.current;
    if (!host || !button || !googleReady) return;
    if (button.parentNode === host) return;
    host.innerHTML = '';
    button.addEventListener('click', () => { engaged.current = 'google'; });
    host.appendChild(button);
  });

  const start = React.useCallback((key) => {
    const instance = instances.current[key];
    if (!instance) return false;
    engaged.current = key;
    // The browser can refuse to open the sheet (Apple Pay: "Page already has an
    // active payment session"). That has to come back as "not started", not
    // escape from the click as an uncaught exception.
    try {
      if (key === 'apple' && instance.begin) { instance.begin(); return true; }
      if (instance.start) { instance.start(); return true; }
    } catch {
      engaged.current = '';
    }
    return false;
  }, []);

  return {
    available, start, googleButtonRef, appleCapable,
  };
}
